import React, { useState, useEffect, useRef, useCallback } from 'react';
import { create as createConfetti } from 'canvas-confetti';
import PropTypes from 'prop-types';

import './CanvasConfetti.scss';

const CanvasConfetti = ({ event, delay, targetElement, haltAnimation }) => {
  const canvasRef = useRef(null);
  const confettiInstance = useRef(null);
  const [top, setTop] = useState(0);

  const approved = event === 'APPROVED';
  const onboarded = event === 'ONBOARDED';

  const initializeConfetti = useCallback(() => {
    if (!canvasRef.current || confettiInstance.current) return;
    confettiInstance.current = createConfetti(canvasRef.current, {
      resize: true,
      useWorker: true,
    });
  }, []);

  useEffect(() => {
    initializeConfetti();
    return () => {
      confettiInstance.current = null;
    };
  }, [initializeConfetti]);

  const fireConfetti = (particleRatio, options = {}) => {
    confettiInstance.current({
      ...options,
      particleCount: Math.floor(200 * particleRatio),
    });
  };

  const handleOnboardedConfetti = useCallback(() => {
    if (!confettiInstance.current) return;

    const launchConfetti = () => {
      const commonOptions = {
        scalar: 2,
        spread: 180,
        particleCount: 30,
        origin: { y: -0.1 },
        startVelocity: -35,
      };

      const confettiPromises = [
        confettiInstance.current({
          ...commonOptions,
          shapes: ['rectangle', 'sphere'],
          colors: ['#FF2929', '#2F2C88'],
        }),
        confettiInstance.current({
          ...commonOptions,
          shapes: ['square', 'circle'],
          colors: ['#2F2C88', '#FF2929'],
        }),
      ];

      Promise.all(confettiPromises).catch(console.error);
    };

    setTimeout(launchConfetti, delay);
  }, [delay]);

  const handleApprovedConfetti = useCallback(() => {
    if (!targetElement || !confettiInstance.current) return;

    const rect = targetElement.getBoundingClientRect();
    if (!rect.width || !rect.height)
      return console.error('Invalid dimensions for the target element');

    canvasRef.current.width = rect.width;
    canvasRef.current.height = rect.height;

    requestAnimationFrame(() => {
      setTop(rect.top - 30);

      fireConfetti(0.25, { spread: 26, startVelocity: 55 });
      fireConfetti(0.2, { spread: 60 });
      fireConfetti(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
      fireConfetti(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
      fireConfetti(0.1, { spread: 120, startVelocity: 45 });
    });
  }, [targetElement]);

  useEffect(() => {
    if (haltAnimation) return;
    if (onboarded) handleOnboardedConfetti();
    if (approved) handleApprovedConfetti();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approved, onboarded, delay, targetElement, haltAnimation]);

  if (haltAnimation) return null;

  return (
    <canvas
      className={`confetti ${approved ? 'approved' : ''}`}
      style={{ top: approved ? top : undefined }}
      ref={canvasRef}
    />
  );
};

CanvasConfetti.defaultProps = {
  delay: 1000,
  haltAnimation: false,
};

CanvasConfetti.propTypes = {
  event: PropTypes.string.isRequired,
  delay: PropTypes.number,
  targetElement: PropTypes.object,
  haltAnimation: PropTypes.bool,
};

export default CanvasConfetti;
